import React, { useState, useEffect } from 'react';
import { Download, BarChart3, FileText, TrendingUp, Calendar, Database, RefreshCw, Package, DollarSign, AlertTriangle } from 'lucide-react';
import { useInventory } from '../context/InventoryContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Badge from '../components/ui/Badge';
import { supabase } from '../lib/supabase';

const ReportsPage: React.FC = () => {
  const { products, categories, transactions, getProductTotalStock, getCategoryName } = useInventory();

  const [reportType, setReportType] = useState('inventory');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportStats, setReportStats] = useState({
    totalProducts: 0,
    totalTransactions: 0,
    totalCategories: 0,
    totalInventoryValue: 0,
    lowStockItems: 0
  });

  useEffect(() => {
    calculateReportStats();
  }, [products, transactions, categories]);

  const calculateReportStats = () => {
    const totalProducts = products.length;
    const totalTransactions = transactions.length;
    const totalCategories = categories.length;
    const totalInventoryValue = products.reduce((sum, product) => {
      const stock = getProductTotalStock(product.id);
      return sum + (stock * product.cost_price);
    }, 0);
    const lowStockItems = products.filter(p => {
      const stock = getProductTotalStock(p.id);
      return stock <= p.min_stock_level;
    }).length;

    setReportStats({
      totalProducts,
      totalTransactions,
      totalCategories,
      totalInventoryValue,
      lowStockItems
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      calculateReportStats();
      setLastUpdated(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const generateCsv = () => {
    let csv = '';
    let filename = '';
    const timestamp = new Date().toISOString().split('T')[0];

    if (reportType === 'inventory') {
      csv = 'Product Name,SKU,Category,Price,Cost Price,Current Stock,Location Stock,Min Stock Level,Status\n';
      products.forEach(product => {
        const categoryName = getCategoryName(product.category_id || '');
        const locationStock = getProductTotalStock(product.id);
        const status = locationStock <= product.min_stock_level ? 'Low Stock' : 'In Stock';
        csv += `"${product.name}","${product.sku}","${categoryName}",${product.price},${product.cost_price},${product.stock_quantity},${locationStock},${product.min_stock_level},"${status}"\n`;
      });
      filename = `inventory_report_${timestamp}.csv`;
    } else if (reportType === 'transactions') {
      csv = 'Date,Time,Product ID,Product Name,SKU,Transaction Type,Quantity,Notes\n';
      transactions.forEach(transaction => {
        const product = products.find(p => p.id === transaction.product_id);
        const date = new Date(transaction.date);
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString();
        csv += `"${dateStr}","${timeStr}","${transaction.product_id}","${product?.name || 'Unknown'}","${product?.sku || 'N/A'}","${transaction.type}",${transaction.quantity},"${transaction.notes || ''}"\n`;
      });
      filename = `transactions_report_${timestamp}.csv`;
    } else if (reportType === 'valuation') {
      csv = 'Product Name,SKU,Current Stock,Location Stock,Cost Price,Total Cost,Retail Price,Total Retail,Potential Profit,Profit Margin %\n';
      let totalCostSum = 0;
      let totalRetailSum = 0;
      products.forEach(product => {
        const locationStock = getProductTotalStock(product.id);
        const actualStock = Math.max(locationStock, product.stock_quantity);
        const totalCost = actualStock * product.cost_price;
        const totalRetail = actualStock * product.price;
        const potentialProfit = totalRetail - totalCost;
        const profitMargin = totalRetail > 0 ? ((potentialProfit / totalRetail) * 100).toFixed(2) : '0.00';
        totalCostSum += totalCost;
        totalRetailSum += totalRetail;
        csv += `"${product.name}","${product.sku}",${product.stock_quantity},${locationStock},${product.cost_price.toFixed(2)},${totalCost.toFixed(2)},${product.price.toFixed(2)},${totalRetail.toFixed(2)},${potentialProfit.toFixed(2)},${profitMargin}\n`;
      });
      const totalProfit = totalRetailSum - totalCostSum;
      csv += `\n"TOTAL","","","","",${totalCostSum.toFixed(2)},"",${totalRetailSum.toFixed(2)},${totalProfit.toFixed(2)},""\n`;
      filename = `inventory_valuation_${timestamp}.csv`;
    } else if (reportType === 'lowstock') {
      csv = 'Product Name,SKU,Category,Current Stock,Location Stock,Min Stock Level,Shortage,Status\n';
      const lowStockProducts = products.filter(p => {
        const stock = getProductTotalStock(p.id);
        return stock <= p.min_stock_level;
      });
      lowStockProducts.forEach(product => {
        const categoryName = getCategoryName(product.category_id || '');
        const locationStock = getProductTotalStock(product.id);
        const shortage = product.min_stock_level - locationStock;
        const status = locationStock === 0 ? 'Out of Stock' : 'Low Stock';
        csv += `"${product.name}","${product.sku}","${categoryName}",${product.stock_quantity},${locationStock},${product.min_stock_level},${shortage},"${status}"\n`;
      });
      filename = `low_stock_report_${timestamp}.csv`;
    } else if (reportType === 'category') {
      csv = 'Category,Total Products,Total Stock,Total Value,Avg Price\n';
      categories.forEach(category => {
        const categoryProducts = products.filter(p => p.category_id === category.id);
        const totalProducts = categoryProducts.length;
        const totalStock = categoryProducts.reduce((sum, p) => sum + getProductTotalStock(p.id), 0);
        const totalValue = categoryProducts.reduce((sum, p) => sum + (getProductTotalStock(p.id) * p.cost_price), 0);
        const avgPrice = totalProducts > 0 ? (categoryProducts.reduce((sum, p) => sum + p.price, 0) / totalProducts) : 0;
        csv += `"${category.name}",${totalProducts},${totalStock},${totalValue.toFixed(2)},${avgPrice.toFixed(2)}\n`;
      });
      filename = `category_report_${timestamp}.csv`;
    }

    csv += `\n\n"Report Generated:","${new Date().toLocaleString()}"\n`;
    csv += `"Data Source:","Supabase Database"\n`;
    csv += `"Total Records:","${reportType === 'inventory' ? products.length : reportType === 'transactions' ? transactions.length : reportType === 'category' ? categories.length : products.filter(p => getProductTotalStock(p.id) <= p.min_stock_level).length}"\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setLastUpdated(new Date());
  };
  
  const reportOptions = [
    { value: 'inventory', label: 'Inventory Status Report' },
    { value: 'transactions', label: 'Transaction History Report' },
    { value: 'valuation', label: 'Inventory Valuation Report' },
    { value: 'lowstock', label: 'Low Stock Alert Report' },
    { value: 'category', label: 'Category Analysis Report' },
  ];
  
  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/669610/pexels-photo-669610.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Business reports with charts, graphs, and financial analytics"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Reports</h1>
              <p className="text-lg opacity-90">
                Generate comprehensive analytics and business intelligence reports
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  <span>Advanced Analytics</span>
                </div>
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Custom Reports</span>
                </div>
                <div className="flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  <span>Performance Insights</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Products</p>
                <p className="text-2xl font-bold text-gray-900">{reportStats.totalProducts}</p>
              </div>
              <Package className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Categories</p>
                <p className="text-2xl font-bold text-gray-900">{reportStats.totalCategories}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{reportStats.totalTransactions}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Inventory Value</p>
                <p className="text-2xl font-bold text-gray-900">${reportStats.totalInventoryValue.toFixed(0)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-gray-900">{reportStats.lowStockItems}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Generate Reports</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span>Last updated: {lastUpdated.toLocaleString()}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                isLoading={isRefreshing}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="max-w-md">
            <Select
              label="Report Type"
              options={reportOptions}
              value={reportType}
              onChange={(value) => setReportType(value)}
              fullWidth
            />
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500">
            {reportType === 'inventory' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Inventory Status Report</h3>
                  <Badge variant="secondary">{products.length} Products</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Comprehensive overview of current inventory with real-time stock levels and location tracking.
                </p>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Product names, SKUs, and categories</li>
                  <li>Current stock and location-based stock totals</li>
                  <li>Minimum stock levels and status indicators</li>
                  <li>Price and cost price information</li>
                  <li>Stock status (In Stock / Low Stock)</li>
                </ul>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <Database className="h-3 w-3" />
                  <span>Data Source: Supabase (supplies, product_locations tables)</span>
                </div>
              </div>
            )}

            {reportType === 'transactions' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Transaction History Report</h3>
                  <Badge variant="secondary">{transactions.length} Records</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Complete audit trail of all inventory movements with timestamps and detailed transaction information.
                </p>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Date and time stamps for each transaction</li>
                  <li>Transaction types (stock in/out/adjustment)</li>
                  <li>Product information with SKUs</li>
                  <li>Quantity changes and adjustments</li>
                  <li>Detailed transaction notes</li>
                </ul>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <Database className="h-3 w-3" />
                  <span>Data Source: Supabase (transactions, supplies tables)</span>
                </div>
              </div>
            )}

            {reportType === 'valuation' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Inventory Valuation Report</h3>
                  <Badge variant="success">${reportStats.totalInventoryValue.toFixed(2)}</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Financial analysis of inventory value, profitability metrics, and margin calculations.
                </p>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Current and location-based stock quantities</li>
                  <li>Cost price and total cost valuations</li>
                  <li>Retail price and total retail valuations</li>
                  <li>Potential profit and margin percentages</li>
                  <li>Grand total summary row</li>
                </ul>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <Database className="h-3 w-3" />
                  <span>Data Source: Supabase (supplies, product_locations tables)</span>
                </div>
              </div>
            )}

            {reportType === 'lowstock' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Low Stock Alert Report</h3>
                  <Badge variant="danger">{reportStats.lowStockItems} Items</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Critical inventory alerts identifying products below minimum stock levels requiring immediate attention.
                </p>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Product names, SKUs, and categories</li>
                  <li>Current stock and location stock levels</li>
                  <li>Minimum stock level thresholds</li>
                  <li>Shortage quantities calculated</li>
                  <li>Status indicators (Low Stock / Out of Stock)</li>
                </ul>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <Database className="h-3 w-3" />
                  <span>Data Source: Supabase (supplies, product_locations tables)</span>
                </div>
              </div>
            )}

            {reportType === 'category' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Category Analysis Report</h3>
                  <Badge variant="secondary">{categories.length} Categories</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Comprehensive breakdown of inventory metrics grouped by product categories.
                </p>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Category names and product counts</li>
                  <li>Total stock quantities per category</li>
                  <li>Total inventory value by category</li>
                  <li>Average product pricing</li>
                  <li>Category performance metrics</li>
                </ul>
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <Database className="h-3 w-3" />
                  <span>Data Source: Supabase (categories, supplies, product_locations tables)</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gray-50">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Database className="h-4 w-4" />
              <span className="font-medium">Live Data</span>
            </div>
            <p className="text-xs text-gray-500">
              Reports include timestamps, data sources, and record counts. All data is current as of download time.
            </p>
          </div>
          <Button onClick={generateCsv} className="w-full sm:w-auto">
            <Download className="h-4 w-4 mr-2" />
            Download CSV Report
          </Button>
        </CardFooter>
      </Card>

      {/* Data Accuracy Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Report Accuracy & Data Sources</h4>
              <div className="space-y-2 text-xs text-gray-700">
                <p>
                  <strong>Data Freshness:</strong> All reports are generated in real-time from the Supabase database with current data as of the moment of download.
                </p>
                <p>
                  <strong>Data Sources:</strong> Reports aggregate data from multiple tables including supplies, product_locations, transactions, categories, sales_orders, and purchase_orders.
                </p>
                <p>
                  <strong>Validation:</strong> Stock quantities reflect both system stock and location-based totals. Low stock alerts are calculated using actual location stock levels.
                </p>
                <p>
                  <strong>Timestamps:</strong> Each report includes generation timestamp, data source citations, and total record counts for verification.
                </p>
                <p>
                  <strong>Format:</strong> Reports are exported as CSV files with standardized headers and can be imported into Excel, Google Sheets, or other analytics tools.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;